import { useAtom } from 'jotai';
import type { ReactNode } from 'react';
import { useCallback, useMemo } from 'react';
import { ExposedLogicalDeviceSyncConfigurationModal } from '../components/exposed-logical-device-sync-configuration-modal/ExposedLogicalDeviceSyncConfigurationModal';
import { openSyncConfigurationModalAtom } from '../components/logical-device-sync-configuration-modal/LogicalDeviceSyncConfigurationModal.atom';
import type { LogicalDeviceSyncConfigurationModalVariant } from '@ingenico/type-mfe-contracts/domains';

export interface UseSyncConfigurationToSingleDeviceReturn {
  readonly syncConfigurationModalComponent: ReactNode;
  readonly handleSyncConfiguration: () => void;
}

export interface UseSyncConfigurationToSingleDeviceParams {
  readonly logicalDeviceIds: string[];
  readonly onSyncConfiguration?: () => void;
  readonly variant?: LogicalDeviceSyncConfigurationModalVariant;
}

export function useSyncConfigurationToSingleDevice({
  logicalDeviceIds,
  onSyncConfiguration,
  variant,
}: UseSyncConfigurationToSingleDeviceParams): UseSyncConfigurationToSingleDeviceReturn {
  const [isModalOpen, setIsModalOpen] = useAtom(openSyncConfigurationModalAtom);

  const onCancel = useCallback(() => {
    setIsModalOpen(false);
  }, [setIsModalOpen]);

  const syncConfigurationModalComponent = useMemo(() => {
    return (
      <ExposedLogicalDeviceSyncConfigurationModal
        isOpen={isModalOpen}
        logicalDeviceIds={logicalDeviceIds}
        onClose={onCancel}
        onSuccess={onSyncConfiguration}
        variant={variant}
      />
    );
  }, [isModalOpen, onCancel, onSyncConfiguration, variant, logicalDeviceIds]);

  const handleSyncConfiguration = useCallback(() => {
    setIsModalOpen(true);
  }, [setIsModalOpen]);

  return {
    syncConfigurationModalComponent,
    handleSyncConfiguration,
  };
}
